import { Component, Input, ViewChild, inject, output } from '@angular/core';
import { EndUserSearchResource } from '../../../resources/end-user-search.resource';
import { ZiroFormAbstractDirective } from '../../../../forms/ziro-form-abstract.directive';
import {
  ExpressionEditorKeywordSearchEntity,
  ExpressionEditorKeywordSearchFormData,
} from '../call-logs-query-editor-keyword.component';
import {
  ZiroSelectComponent,
  ZiroSelectConfig,
  ZiroSelectOption,
} from '../../../../forms/fields/select/ziro-select.component';
import { ZiroFormConfig, ZiroFormsValidationState } from '../../../../forms/ziro-forms-models';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { EndUserResult } from '../../../models/generated/ziroModels';
import { ZiroFormStateService } from '../../../../forms/ziro-form-state.service';

@Component({
  selector: 'ziro-expression-editor-keyword-username-search',
  templateUrl: './expression-editor-keyword-username-search.component.html',
  styles: [],
  standalone: false,
})
export class ExpressionEditorKeywordUsernameSearchComponent extends ZiroFormAbstractDirective<
  ExpressionEditorKeywordSearchEntity,
  ExpressionEditorKeywordSearchFormData
> {
  protected _ziroFormStateService: ZiroFormStateService;
  private endUserSearchResource = inject(EndUserSearchResource);

  // TODO: Skipped for migration because:
  //  This input is inherited from a superclass, but the parent cannot be migrated.
  @Input() entity: ExpressionEditorKeywordSearchEntity;
  readonly searchTermWasUsed = output();
  @ViewChild(ZiroSelectComponent) selectField: ZiroSelectComponent;

  formConfig = {
    fields: {
      'keyword-search': {
        dataAutomation: 'username-select',
        componentConfig: new ZiroSelectConfig({
          asyncOptionsFn: this.searchUsername.bind(this),
          placeholder: 'tkey;reports.call_logs.keyword.search.username',
          clearWithInput: true,
          hideDropdownArrow: true,
          minSearchLength: 3,
          hideClear: true,
          keepSearchInput: true,
        }),
      },
    },
    options: {
      columnClasses: {
        label: 'd-none',
        input: 'col-12',
      },
    },
  } as ZiroFormConfig;

  searchUsername(username: string): Observable<ZiroSelectOption[]> {
    return this.endUserSearchResource.searchByQ(username).pipe(
      map((results: EndUserResult[]) => {
        const users: ZiroSelectOption[] = results.map((result: EndUserResult) => {
          return {
            label: result.ref.username,
            value: result.ref.username,
          };
        });
        const uniqueUsers: ZiroSelectOption[] = Array.from(
          new Map(users.map((user: ZiroSelectOption) => [user.label, user])).values(),
        );
        return uniqueUsers.sort(
          (result1: { label: string; value: string }, result2: { label: string; value: string }) => {
            return result1.label.localeCompare(result2.label);
          },
        );
      }),
    );
  }

  protected submit(): Observable<any> {
    return null;
  }

  toFormData = (entity: ExpressionEditorKeywordSearchEntity): ExpressionEditorKeywordSearchFormData => {
    return {
      'keyword-search': {
        label: entity.extensionUsernameOrDeviceName.value,
        value: entity.extensionUsernameOrDeviceName.value,
      },
    } as ExpressionEditorKeywordSearchFormData;
  };

  toEntity = (formData: ExpressionEditorKeywordSearchFormData): ExpressionEditorKeywordSearchEntity => {
    return {
      extensionUsernameOrDeviceName: { selection: 'username', value: formData['keyword-search'].value },
    } as ExpressionEditorKeywordSearchEntity;
  };

  handleSelectClosed(): void {
    if (typeof this.selectField.fieldData === 'string' && this.selectField.fieldData.length >= 3) {
      // Nothing was selected but we received a search term and we'd like to use it.
      this.ziroFormsUpdateSource.next({
        valid: ZiroFormsValidationState.VALID,
        old: {
          ...this.entity,
        },
        new: {
          extensionUsernameOrDeviceName: {
            selection: 'username',
            value: this.selectField.fieldData,
          },
        },
      });
    }
  }
}
